import { useState } from "react";
import { Trash } from "lucide-react";
import {
  Button,
  Input,
  Label,
  MultiSelect,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { useForm } from "@tellescope/forms";
import {
  ParamsForMethod,
  TellescopeModelFromName,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
} from "@tellescope/tanstack-query";
import { toast } from "sonner";

type Drug = ParamsForMethod<"enduser_eligibility_results", "develop_health_run_benefit_verification">["drugs"][number];

const DEFAULT_DRUGS: Drug[] = [
  {
    name: "Mounjaro",
    dosage: "2.5 mg",
    quantity: 28,
  },
  {
    name: "Ozempic",
    dosage: "0.25 mg",
    quantity: 28,
  },
  {
    name: "Wegovy",
    dosage: "0.25 mg",
    quantity: 28,
  },
];

export interface EligibilitySubmissionFormProps {
  contact: TellescopeModelFromName<"endusers">;
  onSubmit: () => void;
  onCancel?: () => void;
}

export const EligibilitySubmissionForm = ({ contact, onSubmit, onCancel }: EligibilitySubmissionFormProps) => {
  const { t } = useTranslation("contacts");

  const eligibilitySubmissionMutation = useTellescopeMutation({
    model: "enduser_eligibility_results",
    method: "develop_health_run_benefit_verification",

    onSuccess: () => {
      onSubmit?.();
    },
    onError: () => {
      toast.error(t("There was an error submitting the eligibility check."));
    },
  });

  const providersQuery = useTellescopeInfiniteQuery({
    model: "users",
    method: "getSome",
    request: {
      filter: {
        NPI: {
          _exists: true,
        },
      },
    },
    select: (data) =>
      data.pages.flat().sort((a, b) => {
        if (contact.assignedTo?.includes(a.id) && !contact.assignedTo?.includes(b.id)) return -1;
        if (!contact.assignedTo?.includes(a.id) && contact.assignedTo?.includes(b.id)) return 1;
        return 0;
      }),
  });

  const medicationsQuery = useTellescopeInfiniteQuery({
    model: "enduser_medications",
    method: "getSome",
    request: {
      filter: {
        enduserId: contact.id,
      },
    },
    select: (data) => {
      return data.pages.flat().reduce<{
        previous: TellescopeModelFromName<"enduser_medications">[];
        current: TellescopeModelFromName<"enduser_medications">[];
      }>(
        (acc, curr) => {
          if (curr.stoppedTakingAt && curr.stoppedTakingAt.getTime() < Date.now()) {
            acc.previous.push(curr);
          } else {
            acc.current.push(curr);
          }
          return acc;
        },
        { previous: [], current: [] }
      );
    },
  });

  const form = useForm({
    fields: {
      testSubmissionType: {
        type: "select",
        initialValue: "",
      },
      provider: {
        type: "select",
        initialValue: providersQuery?.data?.length === 1 ? providersQuery.data[0]?.id ?? "" : "",
        required: true,
      },
      insuranceType: {
        type: "select",
        initialValue: "Primary",
        required: true,
      },
      diagnosisCodes: {
        type: "multiselect",
        initialValue: [],
      },
      currentlyTaking: {
        type: "multiselect",
        initialValue: medicationsQuery.data?.current.map((item) => item.id) ?? [],
      },
      previouslyTaken: {
        type: "multiselect",
        initialValue: medicationsQuery.data?.previous.map((item) => item.id) ?? [],
      },
      selectedDrugs: {
        type: "multiselect",
        initialValue: DEFAULT_DRUGS.map((drug) => JSON.stringify(drug)),
        required: true,
      },
    },
    validateOn: "change",
    onSubmit: async (fields) => {
      await eligibilitySubmissionMutation.mutateAsync({
        enduserId: contact.id,
        providerUserId: fields.provider,
        insuranceType: fields.insuranceType,
        diagnoses: fields.diagnosisCodes.map((code) => ({ code })),
        drug_history: {
          currently_taking_drugs: fields.currentlyTaking.map((name) => ({ name })),
          previously_taken_drugs: fields.previouslyTaken.map((name) => ({ name })),
        },
        drugs: fields.selectedDrugs.map((drug) => JSON.parse(drug) as Drug),
      });
    },
  });

  const [drugBeingAdded, setDrugBeingAdded] = useState<Drug>({
    name: "",
    dosage: "",
    quantity: 0,
  });

  const addDrug = () => {
    if (!drugBeingAdded.name) return;
    if (!drugBeingAdded.dosage) return;
    if (!drugBeingAdded.quantity) return;
    form.setFieldValue("selectedDrugs", [...form.values.selectedDrugs, JSON.stringify(drugBeingAdded)]);
    setDrugBeingAdded({
      name: "",
      dosage: "",
      quantity: 0,
    });
  };

  const removeDrug = (drugString: string) => {
    form.setFieldValue(
      "selectedDrugs",
      form.values.selectedDrugs.filter((selectedDrug) => selectedDrug !== drugString)
    );
  };

  return (
    <form className="flex-1 overflow-y-auto space-y-6 p-2" {...form.getFormProps()}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2 p-1">
          <Label htmlFor="test-submission-type">{t("Test Submission Type")}</Label>
          <Select {...form.getCustomFieldProps("testSubmissionType")}>
            <SelectTrigger id="test-submission-type" className="w-full">
              <SelectValue placeholder="Submit real eligibility check" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="drugs_covered__prior_auth_required__has_copay">
                {t("Drugs Covered, Prior Auth Required, Has Copay")}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 p-1">
          <Label htmlFor="provider">{t("Provider")}</Label>
          <Select {...form.getCustomFieldProps("provider")}>
            <SelectTrigger id="provider" className="w-full">
              <SelectValue placeholder="Select provider" />
            </SelectTrigger>
            <SelectContent>
              {providersQuery.data?.map((provider) => (
                <SelectItem key={provider.id} value={provider.id}>
                  {provider.fname} {provider.lname}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 p-1">
          <Label htmlFor="insurance-type">{t("Insurance Type")}</Label>
          <Select {...form.getCustomFieldProps("insuranceType")}>
            <SelectTrigger id="insurance-type" className="w-full">
              <SelectValue placeholder="Select insurance type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Primary">{t("Primary")}</SelectItem>
              {!!contact.insuranceSecondary && <SelectItem value="Secondary">{t("Secondary")}</SelectItem>}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2 p-1">
          <Label htmlFor="diagnosis-codes">{t("Diagnosis Codes")}</Label>
          <MultiSelect
            id="diagnosis-codes"
            options={
              contact.diagnoses?.map((diagnosis, i) => ({
                label: diagnosis.code ?? t("Unknown Diagnosis"),
                value: diagnosis.id ?? `diagnosis-${i}`,
              })) ?? []
            }
            {...form.getCustomFieldProps("diagnosisCodes")}
            placeholder={t("Select diagnosis codes")}
          />
        </div>

        <div className="space-y-2 p-1">
          <Label htmlFor="currently-taking">{t("Currently Taking Drugs")}</Label>
          <MultiSelect
            id="currently-taking"
            options={
              medicationsQuery.data?.current.map((item) => ({
                label: item.title,
                value: item.id,
              })) ?? []
            }
            {...form.getCustomFieldProps("currentlyTaking")}
            placeholder="Select current medications"
          />
        </div>

        <div className="space-y-2 p-1">
          <Label htmlFor="previously-taken">{t("Previously Taken Drugs")}</Label>
          <MultiSelect
            id="previously-taken"
            options={
              medicationsQuery.data?.previous.map((item) => ({
                label: item.title,
                value: item.id,
              })) ?? []
            }
            {...form.getCustomFieldProps("previouslyTaken")}
            placeholder="Select previous medications"
          />
        </div>
      </div>

      {/* Drug Selection Section */}
      <div className="space-y-4 border-t pt-6">
        <h3 className="text-lg font-medium">{t("Add Drugs to Check")}</h3>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label
              htmlFor="drug-name"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t("Drug Name")}
            </Label>
            <Input
              id="drug-name"
              placeholder="Enter drug name"
              value={drugBeingAdded.name}
              onChange={(e) => setDrugBeingAdded({ ...drugBeingAdded, name: e.target.value })}
              className="h-10"
            />
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="dosage"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t("Dosage")}
            </Label>
            <Input
              id="dosage"
              placeholder="e.g. 500mg"
              value={drugBeingAdded.dosage}
              onChange={(e) => setDrugBeingAdded({ ...drugBeingAdded, dosage: e.target.value })}
              className="h-10"
            />
          </div>

          <div className="space-y-2">
            <Label
              htmlFor="quantity"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {t("Quantity")}
            </Label>
            <div className="flex gap-2">
              <Input
                id="quantity"
                placeholder="e.g. 30 tablets"
                value={drugBeingAdded.quantity}
                onChange={(e) => setDrugBeingAdded({ ...drugBeingAdded, quantity: parseInt(e.target.value) })}
                className="h-10"
              />
              <Button onClick={() => addDrug()} variant="outline" className="h-10 whitespace-nowrap">
                {t("Add Drug")}
              </Button>
            </div>
          </div>
        </div>

        {/* Selected Drugs List */}
        {form.values.selectedDrugs.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium">{t("Selected Drugs:")}</h4>
            {form.values.selectedDrugs.map((drug) => {
              const { name, dosage, quantity } = JSON.parse(drug) as Drug;
              return (
                <div key={drug} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium">{name}</div>
                    <div className="flex items-center">
                      <span className="text-sm text-foreground pr-2">{dosage}</span>
                      <span className="text-sm text-muted-foreground">{quantity}</span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeDrug(drug)}
                    className="text-foreground hover:text-foreground size-9"
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-4 border-t gap-2">
        {!!onCancel && (
          <Button onClick={onCancel} className="w-full md:w-auto" variant="outline">
            {t("Cancel")}
          </Button>
        )}
        <Button type="submit" className="w-full md:w-auto" disabled={!form.isValid}>
          {t("Submit Eligibility Check")}
        </Button>
      </div>
    </form>
  );
};
